import React from "react";
import { DndContext, closestCenter, DragEndEvent } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useTranslation } from "react-i18next";
import { SortableStoryItemProps, StoryListProps } from "../../types/screensTypes";
import StoryCard from "../cards/MuseumStoryCard";
import styles from "./MuseumStoryList.module.css";

// SortableItem Component (Draggable Story Item)
const SortableItem: React.FC<SortableStoryItemProps> = ({ id, clickableZone, onClick, onDelete }) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes}>
            <StoryCard
                name={clickableZone.internalName || t("story")}
                position={clickableZone.position}
                onClick={onClick}
                onDelete={onDelete}
                dragHandleProps={listeners}
            />
        </div>
    );
};

// StoryList Component (Draggable List of Stories)
const StoryList: React.FC<StoryListProps> = ({
    clickableZones,
    setClickableZones,
    saveClickableZone,
    navigateToNext,
    deleteClickableZone,
}) => {
    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        // Exit if no destination or item didn't change position
        if (!over || active.id === over.id) return;

        const oldIndex = clickableZones.findIndex((clickableZone) => clickableZone.id === active.id);
        const newIndex = clickableZones.findIndex((clickableZone) => clickableZone.id === over.id);

        // Reorder the waypoints
        const reorderedClickableZones = arrayMove(clickableZones, oldIndex, newIndex);

        // Update positions and save new positions if needed
        const updatedClickableZones = reorderedClickableZones.map((clickableZone, index) => {
            const newPosition = index + 1;

            if (clickableZone.position !== newPosition) {
                const tempClickableZone = {
                    ...clickableZone,
                    position: newPosition,
                };

                // Save updated story position
                saveClickableZone(clickableZone.id, newPosition);

                return tempClickableZone;
            }
            return clickableZone;
        });

        // Update the state
        setClickableZones(updatedClickableZones);
    };

    return (
        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={clickableZones.map((zone) => zone.id)} strategy={verticalListSortingStrategy}>
                <div className={styles.list}>
                    {clickableZones.map((clickableZone) => (
                        <SortableItem
                            key={clickableZone.id}
                            id={clickableZone.id}
                            clickableZone={clickableZone}
                            onClick={() => navigateToNext(clickableZone.id, clickableZone.subPlansId)}
                            onDelete={() => deleteClickableZone(clickableZone.id)}
                        />
                    ))}
                </div>
            </SortableContext>
        </DndContext>
    );
};

export default StoryList;
